import {
  DisconnectOutlined,
  LinkOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { Button, Modal, Radio } from "antd";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { SluitButton } from "~/components/buttons/SluitButton.jsx";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { parse_timestring_to_timeobject } from "~/lib/localize.js";
import { OGNstartTijd } from "./Ogn.jsx";
import { Tag } from "antd";

// can be both to connect sleep to aerotow, or aerotow to sleep
export const SleepActions = ({
  flight,
  saveFlight,
  allFlights,
  sleepFlight,
  readOnly,
  disconnectSleepFlight,
  addNewSleepFlight,
  connectSleepFlight,
  sleepKisten,
  dayActiveSleepKist=null
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [connectFlightUUID, setConnectFlight] = useState(null);
  const [loadingDisconnect, setLoadingDisconnect] = useState(false);
  const [disconnectModalVisible, setDisconnectModalVisible] = useState(false);
  const [loadingConnect, setLoadingConnect] = useState(false);
  const [loadingAddNew, setLoadAddNew] = useState(false);
  const dispatch = useDispatch();

  if (readOnly) return null;

  // Determine which flights can be connected
  const dep_time = parse_timestring_to_timeobject(flight?.start_tijd);
  const slist = ["tmg", "sep", "tmg-a", "sep-a"];

  const CalcFeasibleFlightsForConnection = () => {
    
    return allFlights &&
    allFlights
      .filter((f) => {
        const correctMethod =
          flight?.start_methode === "sleep"
            ? slist.includes(f.start_methode)
            : !slist.includes(f.start_methode);

        return (
          // Filter no itself, non-connected flights and those with matching methods
          f.uuid !== flight.uuid &&
          !f.sleep_uuid &&
          correctMethod &&
          (!f.start_tijd || (
            dep_time &&
            Math.abs((parse_timestring_to_timeobject(f.start_tijd)).getTime() - dep_time.getTime()) <= 4 * 60 * 1000  // 4 minutes
          ))
        );
      })
      .map((f) => {
        
        return {
          value: f.uuid,
          flight: f,
          label: (
            <p className="row" style={{ gap: 6 }}>
              <OGNstartTijd
                ogn_indicator={f.start_ogn}
                time={f.start_tijd}
                default_value={$t("Niet vertrokken")}
              />{" "}
              - {f.callsign} {f.registratie}{" "}
              <span className="gray">
                {f.type}
                {!f.registratie && $t("Geen vliegtuig geselecteerd")}
              </span>
              {f.gezagvoerder_naam && `- ${f.gezagvoerder_naam}`}
            </p>
          ),
        };
      });
    }

  const feasibleFlightsForConnection = modalVisible && CalcFeasibleFlightsForConnection();

  const checkIfYouCanConnectSleepOrOpenModal = async () => {

    // check for orphaned sleep flights that are connected to this current flight
    const orphanedSleepFlights = allFlights.filter((f) => f.sleep_uuid === flight.uuid);
    if (orphanedSleepFlights && orphanedSleepFlights.length > 0) {
      // we only need to save the current flight sleep uuid with this flight
      console.log('Connecting orphaned sleep flight from memory:', orphanedSleepFlights[0]);
      await saveFlight({
        ...flight,
        sleep_uuid: orphanedSleepFlights[0].uuid
      });
      return;
    }

    // then check if the sleep uuid exists in the backend, it can occur that sometimes the sleep flight is deleted, but the uuid is still in the flight, so we need to check if it exists in the backend
    const data = await api.get(`flights/${flight.uuid}/check_sleep_uuid_exists`);
    if (data.flight != null) {
      // sleep flight does not exist, so we need to remove the sleep uuid from the flight
      console.log('Connecting to orphaned flight from backend: ', data.flight);
      await saveFlight({
        ...flight,
        sleep_uuid: data.flight.uuid,
      });
      dispatch({
        type: "UPDATE_FLIGHT",
        flightData: data.flight,
      });
      return;
    }

    // if you  have no feasible Flights to connect, and you have a dayActiveSleepKist, then directly add new sleep flight with that kist, otherwise open modal
    const feasibleFlights = CalcFeasibleFlightsForConnection();
    if (
      !dayActiveSleepKist ||
      (
        feasibleFlights &&
        feasibleFlights.length > 0 &&
        feasibleFlights.filter((f) => !f.disabled).length > 0
      )
    ) {
      setModalVisible(true);
    } else {
       setLoadAddNew(true);
      await addNewSleepFlight(dayActiveSleepKist);
      setLoadAddNew(false);
    }
  };

  const disconnectSleep = async () => {
    setLoadingDisconnect(true);
    await disconnectSleepFlight();
    setLoadingDisconnect(false);
    setDisconnectModalVisible(false);
    setModalVisible(false);
  };

  // Trigger connecting a sleep flight
  const triggerConnectSleepFlight = async () => {
    setLoadingConnect(true);

    const sleepFlight = feasibleFlightsForConnection.find(
      (f) => f.value === connectFlightUUID,
    );

    if (!sleepFlight) {
      setLoadingConnect(false);
      setModalVisible(false);
      return;
    }

    // set start_method for connected flight, which can only be tmg/sep or sleep in case that we reverse the connection
    let method = sleepFlight.flight.start_methode;
    if (["tmg", "tmg-a"].includes(method)) {
      method = "tmg-a";
    } else if (["sep", "sep-a"].includes(method)) {
      method = "sep-a";
    } else {
      method = "sleep";
    }

    await connectSleepFlight({
      sleep_uuid: connectFlightUUID,
      sleep_start_methode: method,
      flight: sleepFlight.flight,
    });
    setLoadingConnect(false);
    setConnectFlight(null);
    setModalVisible(false);
  };

  const closeModal = () => {
    setConnectFlight(null);
    setLoadingDisconnect(false);
    setLoadingConnect(false);
    setModalVisible(false);
    setDisconnectModalVisible(false);
  };

  const triggerAddNewSleepFlight = async (kist) => {
    setLoadAddNew(true);
    await addNewSleepFlight(kist);
    setLoadAddNew(false);
    closeModal();
  };
  
  return (
    <>
      {/* Connect Modal */}
      <Modal
        title={$t("Voeg sleepvlucht toe")}
        open={modalVisible}
        onCancel={closeModal}
        footer={[
          <SluitButton key="close" onClick={closeModal} />,
          <Button
            key="connect"
            type="primary"
            disabled={
              !feasibleFlightsForConnection ||
              feasibleFlightsForConnection.length === 0 ||
              !connectFlightUUID
            }
            loading={loadingConnect}
            onClick={triggerConnectSleepFlight}
            icon={<LinkOutlined />}
          >
            {$t("Koppel")}
          </Button>,
        ]}
      >
        <p>
          {$t("Huidige vlucht vertrek tijd")}:{" "}
          <span className="bold">
            {flight?.start_tijd || $t("Niet vertrokken")}
          </span>
        </p>

        <p className="subtitle" style={{ marginTop: 24 }}>
          {$t("Selecteer mogelijke sleepvlucht")} ({$t("TMG of SEP vlucht")})
        </p>
        {feasibleFlightsForConnection &&
        feasibleFlightsForConnection.length > 0 ? (
          <div>
            <Radio.Group
              style={{ display: "flex", flexDirection: "column", gap: 12 }}
              onChange={(e) => setConnectFlight(e.target.value)}
              value={connectFlightUUID}
              options={feasibleFlightsForConnection}
            ></Radio.Group>
          </div>
        ) : (
          <p>
            {$t(
              "Geen vluchten binnen 3 minuten van vertrektijd gevonden om te koppelen",
            )}
          </p>
        )}

        <div style={{ marginTop: 24 }}>
          <p className="subtitle">{$t("Of voeg een nieuwe sleepvlucht toe")}</p>

          {sleepKisten &&
            sleepKisten.map((kist, idx) => (
              <Button
                key={idx}
                style={{ marginRight: 8 }}
                loading={loadingAddNew}
                onClick={() => triggerAddNewSleepFlight(kist)}
                icon={<PlusOutlined />}
              >
                {kist.callsign ? `${kist.callsign} - ${kist.registratie}` : kist.registratie}
              </Button>
            ))}

          <Button
            style={{ marginTop: 20 }}
            loading={loadingAddNew}
            type='text'
            onClick={triggerAddNewSleepFlight}
            icon={<PlusOutlined />}
          >
            {$t("Toevoegen ander sleepvliegtuig")}
          </Button>
        </div>
      </Modal>

      {/* Disconnect Modal */}
      <Modal
        title={$t("Sleep ontkoppelen")}
        open={disconnectModalVisible}
        onCancel={closeModal}
        footer={[
          <SluitButton key="close" onClick={closeModal} />,
          <Button
            key="disconnect"
            danger
            loading={loadingDisconnect}
            onClick={disconnectSleep}
            icon={<DisconnectOutlined />}
          >
            {$t("Ontkoppel")}
          </Button>,
        ]}
      >
        <p>
          {$t("Zeker weten?")} <br />
          {$t(
            "Wil je de vlucht wilt ontkoppelen (niet verwijderen) van de sleep",
          )}
        </p>
      </Modal>

      {!sleepFlight && (
        <div className="row" style={{ gap: 6, marginTop: 6 }}>
          <Button
            type="primary"
            size="small"
            loading={loadingConnect}
            disabled={!allFlights || !allFlights.length}
            onClick={checkIfYouCanConnectSleepOrOpenModal}
            icon={<LinkOutlined />}
          >
            {$t("Toevoegen")}
          </Button>
        </div>
      )}

      {sleepFlight && (
        <Button
          danger
          icon={<DisconnectOutlined />}
          size="small"
          loading={loadingDisconnect}
          onClick={() => setDisconnectModalVisible(true)}
        >
          {$t("Ontkoppel")}
        </Button>
      )}
    </>
  );
};
